import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useCountries } from '../../hooks/useCountries';
import { apiGet, apiPatch, apiPost } from '../../services/api';
import CountrySearchSelect from '../common/CountrySearchSelect';

const LOCATION_REGIONS = ['Worldwide', 'Europe only', 'US only', 'Americas', 'EMEA', 'APAC'];
const SALARY_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 50000, label: '€50k+' },
  { value: 75000, label: '€75k+' },
  { value: 100000, label: '€100k+' },
  { value: 150000, label: '€150k+' },
];
const SENIORITY_LEVELS = ['entry', 'mid', 'senior', 'lead'];

const EMPTY_FORM = {
  categoryId: '',
  locationType: 'any',
  locationValue: '',
  salaryMin: '',
  seniority: {},
  frequency: 'weekly',
};

function formatFrequency(freq) {
  return freq === 'daily' ? 'Daily' : 'Weekly';
}

export default function CandidateJobAlertsTab({ token, isActive, handleAuthError }) {
  const { countries, loading: countriesLoading } = useCountries();
  const [alerts, setAlerts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const activeCount = alerts.filter((a) => a.is_active).length;
  const atLimit = activeCount >= 5;

  const loadAlerts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/v1/candidate/job-alerts', token);
      if (handleAuthError?.(res)) return;
      if (!res.ok) {
        setError('Could not load job alerts.');
        return;
      }
      const json = await res.json();
      setAlerts(json.data ?? []);
    } catch {
      setError('Could not load job alerts.');
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await apiGet('/api/v1/categories');
      if (res.ok) {
        const json = await res.json();
        setCategories(json.data ?? []);
      }
    } catch {
      // categories optional for display
    }
  }, []);

  useEffect(() => {
    if (!isActive || !token) return;
    loadAlerts();
    loadCategories();
  }, [isActive, token, loadAlerts, loadCategories]);

  const buildPayload = () => {
    const seniority = SENIORITY_LEVELS.filter((level) => form.seniority[level]);
    return {
      category_id: form.categoryId ? Number(form.categoryId) : null,
      location_type:
        form.locationType === 'any' ? null : form.locationType,
      location_value:
        form.locationType === 'any' || !form.locationValue ? null : form.locationValue,
      salary_min: form.salaryMin ? Number(form.salaryMin) : null,
      seniority: seniority.length > 0 ? seniority : null,
      frequency: form.frequency,
    };
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!token || atLimit) return;
    setSaving(true);
    setFormErrors({});
    setError('');
    try {
      const res = await apiPost('/api/v1/candidate/job-alerts', buildPayload(), token);
      const data = await res.json().catch(() => ({}));
      if (handleAuthError?.(res)) return;
      if (res.status === 422) {
        setFormErrors(data.errors ?? {});
        return;
      }
      if (!res.ok) {
        setError('Could not create alert.');
        return;
      }
      setForm(EMPTY_FORM);
      await loadAlerts();
    } catch {
      setError('Could not create alert.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!token) return;
    setError('');
    try {
      const res = await fetch(
        `${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}/api/v1/candidate/job-alerts/${id}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (handleAuthError?.(res)) return;
      if (!res.ok) {
        setError('Could not delete alert.');
        return;
      }
      await loadAlerts();
    } catch {
      setError('Could not delete alert.');
    }
  };

  const handleToggle = async (alert) => {
    if (!token) return;
    if (!alert.is_active && atLimit) return;
    setError('');
    try {
      const res = await apiPatch(
        `/api/v1/candidate/job-alerts/${alert.id}`,
        { is_active: !alert.is_active },
        token
      );
      const data = await res.json().catch(() => ({}));
      if (handleAuthError?.(res)) return;
      if (res.status === 422) {
        setError(data.errors?.alerts?.[0] || 'Could not update alert.');
        return;
      }
      if (!res.ok) {
        setError('Could not update alert.');
        return;
      }
      await loadAlerts();
    } catch {
      setError('Could not update alert.');
    }
  };

  const updateSeniority = (level, checked) => {
    setForm((prev) => ({
      ...prev,
      seniority: { ...prev.seniority, [level]: checked },
    }));
  };

  if (loading && alerts.length === 0) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" data-testid="rwm-alert-loading">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="rwm-candidate-alerts">
      {error ? (
        <Alert variant="danger" className="mb-3" data-testid="rwm-alert-error">
          {error}
        </Alert>
      ) : null}

      {atLimit ? (
        <Alert variant="warning" className="mb-3" data-testid="rwm-alert-max-warning">
          You have reached the maximum of 5 active job alerts. Pause or delete an alert to create a
          new one.
        </Alert>
      ) : null}

      {alerts.length === 0 ? (
        <p className="text-muted mb-4" data-testid="rwm-alert-empty">
          No job alerts yet. Create one below to get notified of new matching jobs.
        </p>
      ) : (
        <div className="mb-4">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className="border rounded p-3 mb-3 rwm-candidate-alerts__item"
              data-testid={`rwm-alert-item-${alert.id}`}
            >
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                <div>
                  <p className="mb-1 fw-semibold">{alert.criteria_summary}</p>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge bg="info" text="dark">
                      {formatFrequency(alert.frequency)}
                    </Badge>
                    <Badge bg={alert.is_active ? 'success' : 'secondary'}>
                      {alert.is_active ? 'Active' : 'Paused'}
                    </Badge>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleToggle(alert)}
                    data-testid={`rwm-alert-toggle-${alert.id}`}
                  >
                    {alert.is_active ? 'Pause' : 'Resume'}
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(alert.id)}
                    data-testid={`rwm-alert-delete-${alert.id}`}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="h5 mb-3">Create new alert</h2>
      <Form onSubmit={handleCreate} className="rwm-candidate-alerts__form">
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={form.categoryId}
                onChange={(e) => setForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                data-testid="rwm-alert-category"
              >
                <option value="">Any category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Minimum salary</Form.Label>
              <Form.Select
                value={form.salaryMin}
                onChange={(e) => setForm((prev) => ({ ...prev, salaryMin: e.target.value }))}
                data-testid="rwm-alert-salary-min"
              >
                {SALARY_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Label>Location</Form.Label>
            <div className="d-flex flex-wrap gap-3 mb-2">
              <Form.Check
                type="radio"
                name="alert-location-type"
                id="alert-location-any"
                label="Any location"
                checked={form.locationType === 'any'}
                onChange={() =>
                  setForm((prev) => ({ ...prev, locationType: 'any', locationValue: '' }))
                }
                data-testid="rwm-alert-location-type-any"
              />
              <Form.Check
                type="radio"
                name="alert-location-type"
                id="alert-location-eligibility"
                label="Region"
                checked={form.locationType === 'eligibility'}
                onChange={() =>
                  setForm((prev) => ({ ...prev, locationType: 'eligibility', locationValue: '' }))
                }
                data-testid="rwm-alert-location-type-eligibility"
              />
              <Form.Check
                type="radio"
                name="alert-location-type"
                id="alert-location-country"
                label="Specific country"
                checked={form.locationType === 'country'}
                onChange={() =>
                  setForm((prev) => ({ ...prev, locationType: 'country', locationValue: '' }))
                }
                data-testid="rwm-alert-location-type-country"
              />
            </div>
            {form.locationType === 'eligibility' ? (
              <Form.Select
                value={form.locationValue}
                onChange={(e) => setForm((prev) => ({ ...prev, locationValue: e.target.value }))}
                data-testid="rwm-alert-location-value"
              >
                <option value="">Select region</option>
                {LOCATION_REGIONS.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </Form.Select>
            ) : null}
            {form.locationType === 'country' ? (
              <CountrySearchSelect
                countries={countries}
                loading={countriesLoading}
                value={form.locationValue}
                onChange={(value) => setForm((prev) => ({ ...prev, locationValue: value }))}
              />
            ) : null}
          </Col>

          <Col xs={12}>
            <Form.Label>Seniority</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {SENIORITY_LEVELS.map((level) => (
                <Form.Check
                  key={level}
                  type="checkbox"
                  id={`alert-seniority-${level}`}
                  label={level.charAt(0).toUpperCase() + level.slice(1)}
                  checked={Boolean(form.seniority[level])}
                  onChange={(e) => updateSeniority(level, e.target.checked)}
                  data-testid={`rwm-alert-seniority-${level}`}
                />
              ))}
            </div>
          </Col>

          <Col xs={12}>
            <Form.Label>Frequency</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                name="alert-frequency"
                id="alert-frequency-daily"
                label="Daily"
                checked={form.frequency === 'daily'}
                onChange={() => setForm((prev) => ({ ...prev, frequency: 'daily' }))}
                data-testid="rwm-alert-frequency-daily"
              />
              <Form.Check
                type="radio"
                name="alert-frequency"
                id="alert-frequency-weekly"
                label="Weekly"
                checked={form.frequency === 'weekly'}
                onChange={() => setForm((prev) => ({ ...prev, frequency: 'weekly' }))}
                data-testid="rwm-alert-frequency-weekly"
              />
            </div>
          </Col>

          {formErrors.frequency ? (
            <Col xs={12}>
              <div className="text-danger small">{formErrors.frequency[0]}</div>
            </Col>
          ) : null}
          {formErrors.alerts ? (
            <Col xs={12}>
              <div className="text-danger small">{formErrors.alerts[0]}</div>
            </Col>
          ) : null}

          <Col xs={12}>
            <Button
              type="submit"
              variant="info"
              className="text-white"
              disabled={saving || atLimit}
              data-testid="rwm-alert-create-btn"
            >
              {saving ? 'Creating…' : 'Create Alert'}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
